import { useContext, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import { amountString, toast } from '../../utils'

import { StoresContext } from '../../provider/StoreProvider'
import { ModalContext } from '../../provider/modalProvider'

import { useNavigate } from '../../hooks/useNavigate'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { AlignCenterItem, RowBetween, ColumnItem, CenterItem } from '../../components/common'
import { Button } from '../../components/button'
import { Card } from '../../components/card/Card'
import { Label } from '../../components/label'
import { Text } from '../../components/text'
import { DivideLine } from '../../components/divide'
import { Header } from '../../components/header'
import { DetailContent } from '../../components/pagingList'
import { Spinner } from '../../components/spinner/Spinner'
import { CheckVerificationModal, CreateAccountModal } from '../settings/modal'

import { ReactComponent as WalletIcon } from '../../assets/gui/investment/wallet_42.svg'
import { ReactComponent as CoinIcon } from '../../assets/gui/investment/coin_42.svg'
import { ReactComponent as RightLargeArrowIcon } from '../../assets/ico/common/arrow/right/arrow_right_black_large.svg'

export const MyPage = observer(() => {
  const {
    myStore,
    commonStore,
    authStore: { userInfo, getUserInfo },
  } = useContext(StoresContext)
  const { openModal } = useContext(ModalContext)
  const detailStore = myStore.assetSummaryStore
  const { content: assetSummary } = detailStore

  const navigate = useNavigate()

  useEffect(() => {
    commonStore.setShowTab(true)

    try {
      loadData()
    } catch (err: unknown) {
      toast('페이지를 불러오는데 실패했습니다.', err)
    }
  }, [])

  useEffect(() => {
    if (location.pathname.includes('/myPage')) {
      commonStore.setActiveTab('myPage')
    }
  }, [location])

  const loadData = async () => {
    await getUserInfo()
    await detailStore.load()
  }

  const checkAccount = () => {
    if (!userInfo?.identification) {
      openModal({ id: 'CHECK_VERIFICATION', Component: CheckVerificationModal })
      return false
    }

    if (!userInfo?.withdrawalAccount || !userInfo?.depositAccount) {
      openModal({ id: 'CREATE_ACCOUNT', Component: CreateAccountModal })
      return false
    }

    return true
  }

  const goPurchaseProducts = () => {
    const isAccount = checkAccount()
    if (isAccount) navigate('/myPage/asset')
  }

  const goAccount = () => {
    const isAccount = checkAccount()
    if (isAccount) navigate('/myPage/account')
  }

  const checkWithdrawal = () => {
    if (checkAccount()) navigate('/myPage/account/withdrawal')
  }

  const checkDeposit = () => {
    if (checkAccount()) navigate('/myPage/account/deposit')
  }

  return (
    <Wrap>
      <Header type="SUB" title="투자 내역" isBack={false} />
      <StyledContent backgroundColor="gray_1">
        {userInfo ? (
          <DetailContent detailStore={detailStore}>
            <ColumnItem $gap={16}>
              <Card $direction="column" $gap={16} $padding="24px" $radius={16} $background="white">
                <NavigateRow onClick={goPurchaseProducts}>
                  <AlignCenterItem $gap={16}>
                    <WalletIcon />
                    <ColumnItem $gap={4}>
                      <Text size={16}>총 보유 자산</Text>
                      <Text size={24} weight={600} color="primary_5">
                        {amountString(String(assetSummary?.totalBalance || 0))}원
                      </Text>
                    </ColumnItem>
                  </AlignCenterItem>
                  <RightLargeArrowIcon />
                </NavigateRow>
                <DivideLine />
                <RowBetween>
                  <Text size={16}>청약 신청 수량</Text>
                  <Text size={16} weight={700} color="primary_5">
                    {amountString(String(assetSummary?.subscriptionShares || 0))}주
                  </Text>
                </RowBetween>
                <RowBetween>
                  <Text size={16}>배정 수량</Text>
                  <Text size={16} weight={700} color="primary_5">
                    {amountString(String(assetSummary?.allotmentShares || 0))}주
                  </Text>
                </RowBetween>
                <RowBetween>
                  <Text size={16}>총 보유 작품 수량</Text>
                  <Text size={16} weight={700} color="primary_5">
                    {amountString(String(assetSummary?.productCount || 0))}개
                  </Text>
                </RowBetween>
              </Card>
              <Card $direction="column" $gap={16} $padding="24px" $radius={16} $background="white">
                <NavigateRow onClick={goAccount}>
                  <AlignCenterItem $gap={16}>
                    <CoinIcon />
                    <ColumnItem $gap={4}>
                      <Text size={16}>예치금</Text>
                      <AlignCenterItem $gap={8}>
                        <Text size={24} weight={600} color="primary_5">
                          {amountString(String(assetSummary?.depositBalance || 0))}원
                        </Text>
                        {!assetSummary?.depositBalance && (
                          <Label size="small" backgroundColor="primary_2" textColor="primary_5">
                            예치금을 충전해주세요
                          </Label>
                        )}
                      </AlignCenterItem>
                    </ColumnItem>
                  </AlignCenterItem>
                  <RightLargeArrowIcon />
                </NavigateRow>
                <DivideLine />
                <RowBetween>
                  <Text size={16}>청약 가능 금액</Text>
                  <Text size={16} weight={700} color="primary_5">
                    {amountString(String(assetSummary?.depositBalance || 0))}원
                  </Text>
                </RowBetween>
                <RowBetween>
                  <Text size={16}>출금 가능 금액</Text>
                  <Text size={16} weight={700} color="primary_5">
                    {amountString(String(assetSummary?.withdrawableBalance || 0))}원
                  </Text>
                </RowBetween>
                <ButtonBox>
                  <Button type="secondary" size="small" block={true} onClick={checkWithdrawal}>
                    출금하기
                  </Button>
                  <Button type="primary" size="small" block={true} onClick={checkDeposit}>
                    충전하기
                  </Button>
                </ButtonBox>
              </Card>
            </ColumnItem>
          </DetailContent>
        ) : (
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        )}
      </StyledContent>
    </Wrap>
  )
})

const StyledContent = styled(Content)`
  padding: 16px;
`

const NavigateRow = styled(RowBetween)`
  cursor: pointer;
`

const LoadingContainer = styled(CenterItem)`
  width: 100%;
  height: 100%;
`

const ButtonBox = styled(RowBetween)`
  gap: 8px;
  margin-top: 16px;
`
